/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";
import SelectField from "../../../Components/Masters/SelectField";
import countries from "../../../Pages/Masters/Roles and Permissions/Country.json";

const buildReportingStartYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  const startYear = 2020;
  const totalYears = Math.max(0, lastYear - startYear + 1);
  return Array.from({ length: totalYears }, (_, i) => startYear + i);
};

const buildReportingEndYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 2021;
  const totalYears = Math.max(0, currentYear - startYear + 1);
  return Array.from({ length: totalYears }, (_, i) => startYear + i);
};

const getAllowedReportingEndYears = (startYear) => {
  if (Number.isNaN(startYear)) return [];
  return [startYear + 1];
};

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const inferReportingStartYear = (iso) => {
  const full = toDateInputValue(iso);
  if (!full) return "";
  const [ys, ms, ds] = full.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (m === 4 && d === 1) return ys;
  if (m > 4 || (m === 4 && d > 1)) return String(y);
  if (m < 4) return String(y - 1);
  return String(y);
};

const inferReportingEndYear = (iso) => {
  const full = toDateInputValue(iso);
  if (!full) return "";
  const [ys, ms, ds] = full.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (m === 3 && d === 31) return ys;
  if (m < 3 || (m === 3 && d < 31)) return String(y);
  return String(y + 1);
};

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
  investmentTypeOptions = [],
  investmentTypeDetailsMap = {},
}) => {
  const initialFormData = {
    investmentMaster:
      typeof initialData?.investmentMaster === "object"
        ? initialData?.investmentMaster?._id || ""
        : initialData?.investmentMaster || "",
    investmentType: initialData?.investmentType || "",
    investeeProjectName: initialData?.investeeProjectName || "",
    sector: initialData?.sector || "",
    countryOfOperation: initialData?.countryOfOperation || "",
    equityHoldingPercent: initialData?.equityHoldingPercent || "",
    reportingStartYear: inferReportingStartYear(initialData?.reportingStart),
    reportingEndYear: inferReportingEndYear(initialData?.reportingEnd),
    scope1EmissionsMetricTon: initialData?.scope1EmissionsMetricTon || "",
    scope2EmissionsMetricTon: initialData?.scope2EmissionsMetricTon || "",
    scope3EmissionsMetricTon: initialData?.scope3EmissionsMetricTon || "",
    documents: initialData?.documents || [],
  };

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formDataSnapshot, setFormDataSnapshot] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const MASTER_LOCKED_FIELDS = [
    "investeeProjectName",
    "sector",
    "countryOfOperation",
    "equityHoldingPercent",
  ];

  useEffect(() => {
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles(initialData?.documents || []);
  }, [isOpen, initialData]);

  const fieldConfig = [
    {
      label: "Investment Type",
      name: "investmentType",
      type: "select",
      required: true,
      placeholder: "Tap to select",
    },
    {
      label: "Investee/Project Name",
      name: "investeeProjectName",
      type: "text",
      required: true,
      placeholder: "Enter project name",
    },
    {
      label: "Sector(s) the investee company operates in",
      name: "sector",
      type: "text",
      required: true,
      placeholder: "Enter sector",
    },
    {
      label: "Country of operation",
      name: "countryOfOperation",
      type: "select",
      required: true,
      placeholder: "Enter country of operation",
    },
    {
      label: "Equity Holding (%)",
      name: "equityHoldingPercent",
      type: "text",
      required: true,
      placeholder: "Enter equity holding",
      numericOnly: true,
    },
    {
      label: "Reporting Period Start Date",
      name: "reportingStartYear",
      type: "reportingYear",
      fixedDateText: "1st April",
      required: true,
    },
    {
      label: "Reporting Period End Date",
      name: "reportingEndYear",
      type: "reportingYear",
      fixedDateText: "31st March",
      required: true,
    },
    {
      label: "Scope 1 Emissions (Metric Ton)",
      name: "scope1EmissionsMetricTon",
      type: "text",
      required: true,
      placeholder: "Enter value",
      numericOnly: true,
    },
    {
      label: "Scope 2 Emissions (Metric Ton)",
      name: "scope2EmissionsMetricTon",
      type: "text",
      required: true,
      placeholder: "Enter value",
      numericOnly: true,
    },
    {
      label: "Scope 3 Emissions (Metric Ton)",
      name: "scope3EmissionsMetricTon",
      type: "text",
      required: false,
      placeholder: "Enter value",
      numericOnly: true,
    },
    {
      name: "document",
      type: "docs",
      placeholder: "Enter Document",
    },
  ];

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles([]);
    onClose();
  };

  const isFormChanged = () =>
    JSON.stringify(formData) !== JSON.stringify(formDataSnapshot);

  const handleClose = () => {
    if (!isFormChanged()) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    fieldConfig.forEach((field) => {
      const value = formData[field.name];

      if (field.required) {
        if (
          value === "" ||
          value === null ||
          value === undefined ||
          value === "Tap to select"
        ) {
          newErrors[field.name] = `${field.label} is required.`;
          return;
        }
      }

      if (
        (field.type === "number" || field.numericOnly) &&
        value !== "" &&
        isNaN(Number(value))
      ) {
        newErrors[field.name] = `${field.label} must be a number.`;
      }
    });

    if (formData.reportingStartYear && formData.reportingEndYear) {
      const startYear = parseInt(formData.reportingStartYear, 10);
      const endYear = parseInt(formData.reportingEndYear, 10);
      const allowedEndYears = getAllowedReportingEndYears(startYear);
      if (!Number.isNaN(endYear) && !allowedEndYears.includes(endYear)) {
        newErrors.reportingEndYear =
          "Reporting period end year must be the immediate next year of the start year.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (name, type) => (event) => {
    const { value } = event.target;

    // Check if field should only accept numeric values
    const field = fieldConfig.find((f) => f.name === name);
    const isNumericField = type === "number" || field?.numericOnly;

    if (isNumericField && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      if (name === "reportingStartYear") {
        const startYear = parseInt(value, 10);
        const endYear = parseInt(prev.reportingEndYear, 10);
        const allowedEndYears = getAllowedReportingEndYears(startYear);
        if (!Number.isNaN(endYear) && !allowedEndYears.includes(endYear)) {
          updated.reportingEndYear = "";
        }
      }

      return updated;
    });

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;

    const formDataToSubmit = {
      ...formData,
      reportingStart: formData.reportingStartYear
        ? `${formData.reportingStartYear}-04-01`
        : "",
      reportingEnd: formData.reportingEndYear
        ? `${formData.reportingEndYear}-03-31`
        : "",
      documents: uploadedFiles,
    };
    delete formDataToSubmit.reportingStartYear;
    delete formDataToSubmit.reportingEndYear;

    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: "26rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {fieldConfig.map((field) => (
              <div key={field.name} style={{ margin: "1rem 0" }}>
                {field.type === "docs" ? (
                  <FileField
                    title={formatTitleWithOptional("Upload Documents (optional)")}
                    name={field.name}
                    errors={errors[field.name]}
                    fileUrls={uploadedFiles}
                    progress={uploadProgress[field.name] || []}
                    {...{
                      setUploadedFiles,
                      setUploadProgress,
                      setErrors,
                    }}
                  />
                ) : field.type === "select" && field.name === "investmentType" ? (
                  <SelectField
                    title={field.label}
                    name={field.name}
                    value={formData[field.name]}
                    handleChange={(e) => {
                      const value =
                        e.target.value === "Tap to select" ? "" : e.target.value;
                      const selectedMaster = investmentTypeDetailsMap?.[value] || {};
                      setFormData((prev) => ({
                        ...prev,
                        investmentMaster:
                          value && selectedMaster?._id ? selectedMaster._id : "",
                        [field.name]: value,
                        investeeProjectName:
                          value && selectedMaster.investeeProjectName
                            ? selectedMaster.investeeProjectName
                            : prev.investeeProjectName,
                        sector:
                          value && selectedMaster.sector
                            ? selectedMaster.sector
                            : prev.sector,
                        countryOfOperation:
                          value && selectedMaster.countryOfOperation
                            ? selectedMaster.countryOfOperation
                            : prev.countryOfOperation,
                        equityHoldingPercent:
                          value && selectedMaster.equityHoldingPercent !== undefined
                            ? selectedMaster.equityHoldingPercent
                            : prev.equityHoldingPercent,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        [field.name]: "",
                        investeeProjectName: "",
                        sector: "",
                        countryOfOperation: "",
                        equityHoldingPercent: "",
                      }));
                    }}
                    options={investmentTypeOptions}
                    errors={errors[field.name]}
                  />
                ) : field.type === "select" && field.name === "countryOfOperation" ? (
                  <SelectField
                    title={field.label}
                    name={field.name}
                    value={formData[field.name]}
                    disable={MASTER_LOCKED_FIELDS.includes(field.name)}
                    handleChange={(e) => {
                      const value =
                        e.target.value === "Tap to select" ? "" : e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        [field.name]: value,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        [field.name]: "",
                      }));
                    }}
                    options={countries.countries.map((country) => country.name)}
                    errors={errors[field.name]}
                  />
                ) : field.type === "reportingYear" ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        flexWrap: "nowrap",
                      }}
                    >
                      <TextField
                        size="small"
                        value={field.fixedDateText}
                        disabled
                        sx={{
                          width: 130,
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                            color: "rgba(0, 0, 0, 0.87)",
                          },
                          "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "rgba(0, 0, 0, 0.23)",
                            },
                        }}
                      />
                      <TextField
                        select
                        size="small"
                        required={field.required}
                        sx={{ width: 130 }}
                        value={formData[field.name] || ""}
                        onChange={handleChange(field.name, field.type)}
                        error={Boolean(errors[field.name])}
                        FormHelperTextProps={{
                          sx: { ml: 0, mr: 0, fontSize: "0.875rem" },
                        }}
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="">
                          <em>Select year</em>
                        </MenuItem>
                        {(field.name === "reportingStartYear"
                          ? buildReportingStartYearOptions()
                          : buildReportingEndYearOptions())
                          .filter((yr) => {
                            if (field.name !== "reportingEndYear") return true;
                            const startYear = parseInt(
                              formData.reportingStartYear,
                              10
                            );
                            if (Number.isNaN(startYear)) return true;
                            return getAllowedReportingEndYears(startYear).includes(yr);
                          })
                          .map((yr) => (
                            <MenuItem key={yr} value={String(yr)}>
                              {yr}
                            </MenuItem>
                          ))}
                      </TextField>
                    </Box>
                    {errors[field.name] ? (
                      <Typography
                        variant="body2"
                        color="#D34040"
                        sx={{ mt: 0.5, ml: 0 }}
                      >
                        {errors[field.name]}
                      </Typography>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>

                    <TextField
                      type={field.type === "date" ? "date" : "text"}
                      size="small"
                      fullWidth
                      disabled={MASTER_LOCKED_FIELDS.includes(field.name)}
                      required={field.required}
                      placeholder={field.placeholder || ""}
                      value={formData[field.name] || ""}
                      onChange={handleChange(field.name, field.type)}
                      error={Boolean(errors[field.name])}
                      helperText={errors[field.name] || ""}
                      FormHelperTextProps={{
                        sx: { ml: 0, mr: 0, fontSize: "0.875rem" },
                      }}
                      inputProps={
                        field.type === "date"
                          ? { max: new Date().toISOString().split("T")[0] }
                          : {}
                      }
                    />
                  </>
                )}
              </div>
            ))}
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#1C486B", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;

